import { IProfileModal } from "./ProfileModalInterfaces";
import { Alert, Avatar, Box, Button, Modal, Snackbar } from "@mui/material";
import { modalStyles, useStyles } from "./ProfileModal.styles";
import { useTranslation } from "react-i18next";
import { ChangeEvent, useContext, useState } from "react";
import { uploadUserImage } from "api/queries/usersAPI";
import { CorporateContext } from "contexts/CorporateContext/CorporateContext";
import ChangePasswordModal from "./ChangePasswordModal";
import { translateDefaultEntity } from "utilities/localesUtilities";
import { DefaultTranslationFieldsInTable } from "constants/constants";

const ProfileModal = ({ open, onClose, user }: IProfileModal) => {
    const { refreshCurrentUser } = useContext(CorporateContext);
    const classes = useStyles();
    const { t, i18n } = useTranslation();
    const [file, setFile] = useState<{
        preview: string;
        data: File;
    } | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [showChangePasswordModal, setShowChangePasswordModal] = useState(false);

    const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
        if (!e.target.files) return;
        setIsSaving(true);
        const formData = new FormData();
        formData.set("file", e.target.files[0] as File);
        formData.set("user_id", user?.user_id?.toString() || "");
        try {
            await uploadUserImage(user?.user_id!, formData);
            const img = {
                preview: URL.createObjectURL(e.target.files[0]),
                data: e.target.files[0],
            };
            setFile(img);
            setIsSaving(false);
            setShowSnackbar(true);
        } catch (error) {
            setIsSaving(false);
        }
    };

    const handleOnClose = () => {
        if (file?.data) {
            refreshCurrentUser();
        }
        onClose();
    };

    return (
        <Modal open={open} onClose={handleOnClose} disableEscapeKeyDown>
            <Box sx={modalStyles}>
                <div className={classes.modalHeader}>
                    {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.title")}
                </div>
                <div className={classes.modalBody}>
                    <div className={classes.section}>
                        <div className={classes.modalBodyLeft}>
                            <Avatar className={classes.avatar} src={file?.preview || user?.media?.media_url} />
                            <span className={classes.userImageActionWrapper}>
                                <Button
                                    disabled={isSaving}
                                    variant="text"
                                    component="label"
                                    color="info"
                                    className={classes.userImageAction}
                                >
                                    {t(
                                        "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.change_image",
                                    )}
                                    <input hidden accept="image/*" multiple type="file" onChange={handleFileChange} />
                                </Button>
                            </span>
                        </div>

                        <div className={classes.modalBodyRight}>
                            <div className={classes.item}>
                                <span className={classes.label}>
                                    {t(
                                        "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.name",
                                    )}
                                </span>
                                <span className={classes.value}>{`${user?.first_name} ${user?.last_name}`}</span>
                            </div>
                            <div className={classes.item}>
                                <span className={classes.label}>
                                    {t(
                                        "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.email",
                                    )}
                                </span>
                                <span className={classes.value}>{user?.email}</span>
                            </div>
                            <div className={classes.item}>
                                <span className={classes.label}>
                                    {t(
                                        "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.employee_id",
                                    )}
                                </span>
                                <span className={classes.value}>{user?.employee_number || "N/A"}</span>
                            </div>
                            <div className={classes.item}>
                                <span className={classes.label}>
                                    {t(
                                        "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.user_type",
                                    )}
                                </span>
                                <span className={classes.value}>
                                    {translateDefaultEntity(
                                        i18n.language,
                                        user?.user_type,
                                        "name",
                                        DefaultTranslationFieldsInTable.USER_TYPE,
                                    )}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
                <div className={classes.footer}>
                    <Button
                        variant="outlined"
                        color="error"
                        className={classes.submitButton}
                        onClick={() => setShowChangePasswordModal(true)}
                    >
                        {t(
                            "corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.change_password",
                        )}
                    </Button>
                    <Button variant="contained" color="info" className={classes.submitButton} onClick={handleOnClose}>
                        {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.close")}
                    </Button>
                </div>
                <ChangePasswordModal open={showChangePasswordModal} onClose={() => setShowChangePasswordModal(false)} />
                <Snackbar
                    open={showSnackbar}
                    onClose={() => setShowSnackbar(false)}
                    autoHideDuration={6000}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                >
                    <Alert severity="success" sx={{ width: "100%" }}>
                        {t("corporate_backoffice.corporate_layout.app_drawer.app_toolbar.profile_modal.upload_success")}
                    </Alert>
                </Snackbar>
            </Box>
        </Modal>
    );
};

export default ProfileModal;
